<template>
    <div>
        <div class="flex mt-[42px] justify-between h-[42px]">
            <div
                class="group/domain -m-2 flex items-center gap-3 rounded-lg p-2 text-left font-medium transition-colors duration-150"
            >
                <div
                    v-if="domainState.active"
                    class="h-2 w-2 rounded-full bg-green-500"
                ></div>
                <div v-else class="h-2 w-2 rounded-full bg-gray-400"></div>
                <h1
                    class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
                >
                    {{ reportedDomain.domain }}
                </h1>
            </div>
        </div>

        <!--        <CrumbsBarMinimal :domain="reportedDomain.domain" :source="null" />-->

        <BasicTabs
            :tabs="tabs"
            :tab-key="'domain-settings'"
            class="mt-[21px]"
        />

        <div class="mt-10 grid grid-cols-[260px_1fr] gap-12">
            <div class="flex flex-col">
                <div class="mb-55">
                    <h3 class="text-gray-800 font-semibold text-lg">
                        {{ $t("vue.domain_details") }}
                    </h3>
                    <p class="text-gray-500 text-sm">
                        {{ $t("vue.basic_info_about_your_domain") }}
                    </p>
                </div>

                <div class="mb-135">
                    <h3 class="text-gray-800 font-semibold text-lg">
                        {{ $t("vue.domain_details") }}
                    </h3>
                    <p class="text-gray-500 text-sm">
                        {{ $t("vue.update_your_txt_record") }}
                    </p>
                </div>

                <div>
                    <h3 class="text-gray-800 font-semibold text-lg">
                        {{ $t("vue.remove_domain") }}
                    </h3>
                    <p class="text-gray-500 text-sm">
                        {{ $t("vue.you_can_delete_domain_and_all_reports") }}
                    </p>
                </div>
            </div>

            <div class="space-y-12">
                <div
                    class="bg-white rounded-[12.8px] border border-gray-200 p-6"
                >
                    <div class="flex items-start justify-left gap-70 pb-4">
                        <div>
                            <div class="text-gray-800 font-semibold pl-2">
                                Status
                            </div>

                            <div
                                class="policy-badge"
                                :class="
                                    domainState.active
                                        ? 'badge-active'
                                        : 'badge-inactive'
                                "
                            >
                                {{ domainState.active ? "Active" : "Inactive" }}
                            </div>
                        </div>

                        <div>
                            <div class="text-gray-800 font-semibold">
                                Created
                            </div>
                            <div class="text-gray-500 text-sm">
                                {{ createdAtFormatted }}
                            </div>
                        </div>

                        <div>
                            <div class="text-gray-800 font-semibold">
                                DMARC Policy
                            </div>

                            <div class="text-gray-500 text-sm mt-1">
                                <span
                                    v-if="reportedDomain.dmarcRecord"
                                    class="policy-badge capitalize"
                                    :class="{
                                        'policy-reject':
                                            domainState.dmarcPolicy ===
                                            'reject',
                                        'policy-quarantine':
                                            domainState.dmarcPolicy ===
                                            'quarantine',
                                        'policy-none':
                                            domainState.dmarcPolicy !==
                                                'reject' &&
                                            domainState.dmarcPolicy !==
                                                'quarantine',
                                    }"
                                >
                                    {{ domainState.dmarcPolicy }}
                                </span>

                                <span
                                    v-else
                                    class="policy-badge policy-none capitalize"
                                    >none</span
                                >
                            </div>
                        </div>
                    </div>

                    <div class="w-full h-px bg-gray-200 my-6"></div>
                    <div class="flex justify-end">
                        <button
                            class="relative bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition"
                            :aria-busy="
                                dnsCheckingInProgress ? 'true' : 'false'
                            "
                            @click="checkDnsRecords"
                        >
                            <span
                                v-if="showPulse"
                                :key="pulseKey"
                                class="pulse-ring pointer-events-none absolute -inset-1 rounded-md"
                                aria-hidden="true"
                                @animationend="showPulse = false"
                            />
                            {{ $t("vue.reload_record") }}
                        </button>
                    </div>
                </div>

                <div
                    class="bg-white rounded-[12.8px] border border-gray-200 p-8"
                >
                    <h3 class="text-gray-800 font-semibold text-lg mb-4">
                        {{ $t("vue.configure_your_txt_record") }}
                    </h3>

                    <ol
                        class="list-decimal list-inside text-gray-700 text-sm space-y-1 mb-8"
                    >
                        <li>
                            {{ $t("vue.dmarc_setup_step_1") }}
                        </li>
                        <li>
                            {{ $t("vue.dmarc_setup_step_2") }}
                        </li>
                        <li>
                            {{ $t("vue.dmarc_setup_step_3") }}
                        </li>
                        <li class="text-sm text-gray-700">
                            <span
                                v-html="
                                    t('vue.dmarc_setup_step_4', {
                                        value: '<span class=&quot;font-semibold&quot;>_dmarc</span>',
                                    })
                                "
                            ></span>
                        </li>
                        <li>
                            {{ $t("vue.dmarc_setup_step_5") }}
                        </li>
                        <li>
                            {{ $t("vue.dmarc_setup_step_6") }}
                        </li>
                    </ol>

                    <div class="grid grid-cols-3 gap-4 mb-6">
                        <div>
                            <label class="text-gray-700 font-medium block mb-1">
                                {{ $t("vue.dmarc_policy") }}
                            </label>
                            <SelectInput
                                v-model="policy"
                                :data="policyOptions"
                                placeholder="Select policy"
                            />
                        </div>
                    </div>

                    <h4 class="font-semibold text-gray-800 mb-2">
                        {{ $t("vue.your_txt_record") }}
                    </h4>

                    <textarea
                        class="w-full rounded-lg border p-3 text-sm bg-gray-50"
                        rows="3"
                        readonly
                        :value="txtRecord"
                    ></textarea>

                    <button
                        class="mt-4 text-sm border rounded-md px-3 py-2 hover:bg-gray-100 transition"
                        @click="copyRecord"
                    >
                        {{ $t("vue.copy_to_clipboard") }}
                    </button>
                </div>

                <!-- REMOVE DOMAIN CARD -->
                <div
                    class="bg-white rounded-[12.8px] border border-gray-200 p-8"
                >
                    <h3 class="font-semibold text-gray-800 mb-1">
                        {{ $t("vue.your_domain") }}
                    </h3>
                    <p class="text-gray-500 text-sm mb-6">
                        {{ reportedDomain.domain }}
                        <span class="italic"
                            >({{ $t("vue.this_step_cannot_be_undone") }})</span
                        >
                    </p>

                    <button
                        class="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md"
                        @click="deleteDomain"
                    >
                        {{ $t("vue.delete_domain") }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import route from "ziggy-js";
import type { PropType } from "vue";
import axios from "axios";
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import DateRangeField from "@/Pages/Reports/components/DateRangeField.vue";
import { Globe, Settings } from "lucide-vue-next";
import { create } from "@/composables/useNotifications.ts";
import { useTranslations } from "@/composables/useTranslations.ts";

const { t } = useTranslations();

interface ReportedDomain {
    domain: string;
    active: boolean;
    created_at?: string;
    dmarcPolicy: string;
    dmarcRecord?: string | null;
    account: string;
}

const props = defineProps({
    reportedDomain: {
        type: Object as PropType<ReportedDomain>,
        required: true,
    },
});

const domainState = reactive({
    active: false,
    dmarcPolicy: "none",
    dmarcRecord: null as string | null,
    created_at: null as string | null,
});

onMounted(() => {
    domainState.active = props.reportedDomain.active;
    domainState.dmarcPolicy = props.reportedDomain.dmarcPolicy;
    domainState.dmarcRecord = props.reportedDomain.dmarcRecord ?? null;
    domainState.created_at = props.reportedDomain.created_at ?? null;
});

/* ------------------ STATUS ------------------ */
const createdAtFormatted = computed(() => {
    if (!props.reportedDomain.created_at) return "-";
    return new Date(props.reportedDomain.created_at).toLocaleString();
});

/* ------------------ TXT COPY RECORD ------------------ */
const copyRecord = () => {
    const el = document.createElement("textarea");
    el.value = txtRecord.value;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
};

/* ------------------ TXT RECORD ------------------ */
const policy = ref(props.reportedDomain.dmarcPolicy?.toString() || "none");

const detectedDmarcRecord = ref<string | null>(null);

const detectedDmarcRecordRaw = ref<string | null>(null);

const generatedTxtRecord = computed(
    () =>
        `v=DMARC1; p=${policy.value}; pct=100; rua=mailto:${props.reportedDomain.account}@rua.dmarceye.com; fo=1; ri=86400`,
);

const txtRecord = computed(() => {
    if (detectedDmarcRecordRaw.value) {
        return applyPolicyToDmarcRecord(
            detectedDmarcRecordRaw.value,
            policy.value,
        );
    }

    if (
        props.reportedDomain.dmarcRecord === "" ||
        props.reportedDomain.dmarcRecord === null
    ) {
        return generatedTxtRecord.value;
    }

    return props.reportedDomain.dmarcRecord;
});

const policyOptions = [
    { title: "None", value: "none" },
    { title: "Quarantine", value: "quarantine" },
    { title: "Reject", value: "reject" },
];

const dnsCheckingInProgress = ref(false);
const dnsResult = ref<null | boolean>(null);

/* ------------------ RELOAD DNS ------------------ */

const pulseKey = ref(0);
const showPulse = ref(false);

function triggerPulse() {
    pulseKey.value += 1;
    showPulse.value = true;
}

async function checkDnsRecords() {
    triggerPulse();
    if (dnsCheckingInProgress.value) return;

    dnsCheckingInProgress.value = true;
    dnsResult.value = null;

    try {
        const response = await axios.get(
            route("domains.check-dns", { domain: props.reportedDomain.domain }),
        );

        if (response.data.success) {
            const result = response.data.result;

            domainState.active = result.active;
            domainState.dmarcPolicy = result.dmarc_policy;
            domainState.dmarcRecord = result.dmarc_record ?? null;

            if (result.dmarc_policy) {
                policy.value = result.dmarc_policy;
            }

            detectedDmarcRecord.value = result.dmarc_record ?? null;
            detectedDmarcRecordRaw.value = result.dmarc_record ?? null;

            dnsResult.value = result;
        } else {
            console.warn("DNS checking failed:", response.data.error);
            dnsResult.value = null;
        }
    } catch (error) {
        console.error("Failed to check DNS records:", error);
        dnsResult.value = null;
    } finally {
        dnsCheckingInProgress.value = false;
    }

    create({
        title: t("vue.record_checked"),
        type: "success",
    });
}

/* ------------------ DELETE DOMAIN ------------------ */
const deleteDomain = async () => {
    await axios.delete(
        route("domains.destroy", { domain: props.reportedDomain.domain }),
    );

    window.location.href = route("domains.index");
};

/* ------------------ TAB SWITCHER ------------------ */
const tabs: TabItem[] = [
    {
        title: t("vue.reports"),
        key: "domain-reports",
        icon: Globe,
        url: route("reports.show", { domain: props.reportedDomain.domain }),
    },
    {
        title: t("vue.settings"),
        key: "domain-settings",
        icon: Settings,
        url: route("reports.settings.show", {
            domain: props.reportedDomain.domain,
        }),
    },
];

/* ------------------ DMARC RECORD SELECT ------------------ */

function applyPolicyToDmarcRecord(record: string, newPolicy: string) {
    if (!record) return record;

    if (/;\s*p\s*=/i.test(record) || /\bp\s*=/i.test(record)) {
        return record.replace(
            /(\bp\s*=\s*)(none|quarantine|reject)\b/i,
            `$1${newPolicy}`,
        );
    }

    return record.replace(/v\s*=\s*DMARC1\s*;?/i, (m) => {
        const hasSemi = m.trim().endsWith(";");
        return hasSemi ? `${m} p=${newPolicy};` : `${m}; p=${newPolicy};`;
    });
}
</script>

<style scoped>
.pulse-ring {
    color: rgb(147 197 253 / 0.85);
    box-shadow: 0 0 0 2px currentColor;
    animation: ringPulse 900ms ease-out both;
    transform-origin: center;
}

@keyframes ringPulse {
    0% {
        transform: scale(0.98);
        opacity: 0;
    }
    15% {
        opacity: 1;
    }
    100% {
        transform: scale(1.08);
        opacity: 0;
    }
}
</style>
